import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EyeIcon from '@assets/icons/admin-center/creator/eye.svg';
import PrinterIcon from '@assets/icons/learning-overview/task/printer.svg';
import { BaseButton, BaseInput, HtmlInjection } from '@components';
import { TEST_IDS } from '@constants/testIds.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import { Description, Guideline, RatingsWrapper, TaskWrapper } from '@routes/sidebar/___shared___/styled.tsx';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import { getTaskRoute } from '@routes/sidebar/___shared___/utils.ts';
import { getAssessorTask } from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/model/evaluateTask.slice.ts';
import {
    getLearnerTask,
    getTaskAssessors,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/task.slice.ts';
import type { AttemptLogStatus } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    ATTEMPT_LOG_STATUS,
    LOG_TITLE_COLOR_MAPPER,
    RATING_BACKGROUNDS,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    SHARED_EVALUATION_LOG_APPROVED,
    SHARED_EVALUATION_LOG_ASSESSOR_COMMENTS,
    SHARED_EVALUATION_LOG_ASSESSOR_EVALUATION,
    SHARED_EVALUATION_LOG_CLOSED,
    SHARED_EVALUATION_LOG_DISAPPROVED,
    SHARED_EVALUATION_LOG_EVIDENCE_LABEL,
    SHARED_EVALUATION_LOG_EXEMPTED,
    SHARED_EVALUATION_LOG_EXEMPTION_REJECTED,
    SHARED_EVALUATION_LOG_EXEMPTION_REQUESTED,
    SHARED_EVALUATION_LOG_PAGE_TITLE,
    SHARED_EVALUATION_LOG_PRINT,
    SHARED_EVALUATION_LOG_REDO,
    SHARED_EVALUATION_LOG_REFERENCE,
    SHARED_EVALUATION_LOG_REMOVE,
    SHARED_EVALUATION_LOG_SUBMITTED,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/evaluation-log/constants.ts';
import {
    EvaluationLogSection,
    EvidenceButtonsWrapper,
    EvidenceWrapper,
    Log,
    LogContent,
    LogHeader,
    PageTitle,
    PrintWrapper,
    TaskEvaluationLogWrapper,
    TaskTitle,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/evaluation-log/styled.tsx';
import {
    CommentsContent,
    DateWrapper,
    Label,
    LogInfo,
    LogInfoText,
    LogInfoTextBold,
    LogInfoWrapper,
    Rating,
    ReferenceContentWrapper,
    SectionWrapper,
    StatusInfoWrapper,
    StatusTextWrapper,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/styled.tsx';
import {
    buildEvaluationLogEntries,
    getEvaluationLogEntryViewData,
    getEvaluationLogFieldTexts,
    getLogPersonText,
    getPreviousAssessorText,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/utils.ts';
import { resolveContentUri } from '@shared/model/embededContent.slice.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { isContentEntryValid } from '@utils/embededContents.ts';
import { generateUniqueKeys } from '@utils/generators.ts';
import { openInNewTab } from '@utils/navigation.ts';
import { translateItem } from '@utils/translations.ts';
import { isNil } from 'lodash';

interface Props {
    isAssessorView: boolean;
}

export const TaskEvaluationLog: React.FC<Props> = ({ isAssessorView }) => {
    const { pathwayId, taskId } = useParams<{
        pathwayId: string;
        taskId: string;
    }>();

    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const taskData = useAppSelector((state) =>
        isAssessorView ? state.assessorEvaluateTask.taskData : state.learningTask.taskData,
    );
    const assessors = useAppSelector((state) => state.learningTask.assessors);
    const contentByIdUrl = useAppSelector((state) => state.contentAssets.contentByIdUrl);

    const userPreferencesSettings = getUserPreferencesOrDefaults(userFullInfo);
    const { preferredDateFormat: dateFormat } = userPreferencesSettings;

    const STATUS_TEXT_LABELS = {
        redo: translateItem(SHARED_EVALUATION_LOG_REDO),
        closed: translateItem(SHARED_EVALUATION_LOG_CLOSED),
        exempted: translateItem(SHARED_EVALUATION_LOG_EXEMPTED),
        exemptionRejected: translateItem(SHARED_EVALUATION_LOG_EXEMPTION_REJECTED),
        submitted: translateItem(SHARED_EVALUATION_LOG_SUBMITTED),
    };

    const getLogStatusText = (status: AttemptLogStatus, attemptNumber: number) => {
        const LOG_STATUS_TEXT_MAPPER: Partial<Record<AttemptLogStatus, string>> = {
            [ATTEMPT_LOG_STATUS.PENDING]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.PENDING_EXEMPTION]: translateItem(SHARED_EVALUATION_LOG_EXEMPTION_REQUESTED),
            [ATTEMPT_LOG_STATUS.RECALLED]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.REDO_REQUIRED]: translateItem(SHARED_EVALUATION_LOG_ASSESSOR_EVALUATION),
            [ATTEMPT_LOG_STATUS.CLOSED]: translateItem(SHARED_EVALUATION_LOG_ASSESSOR_EVALUATION),
            [ATTEMPT_LOG_STATUS.EXEMPTED]: translateItem(SHARED_EVALUATION_LOG_EXEMPTED),
            [ATTEMPT_LOG_STATUS.EXEMPTION_REJECTED]: translateItem(SHARED_EVALUATION_LOG_EXEMPTION_REJECTED),
        };

        return LOG_STATUS_TEXT_MAPPER[status] ?? '';
    };

    const ratingScale = taskData?.taskConfiguration?.taskScaleType;

    const ratingScaleOptions: Record<TaskScaleType, string[]> = {
        [TASK_SCALE_TYPE.TwoPointApprove]: [
            translateItem(SHARED_EVALUATION_LOG_DISAPPROVED),
            translateItem(SHARED_EVALUATION_LOG_APPROVED),
        ],
        [TASK_SCALE_TYPE.TwoPointConfidence]: [t`Not Confident`, t`Confident`],
        [TASK_SCALE_TYPE.FivePointCompetence]: [
            t`Unsatisfactory`,
            t`Needs improvement`,
            t`Meets expectations`,
            t`Exceeds expectations`,
            t`Exceptional`,
        ],
    };

    const ratings = ratingScale ? ratingScaleOptions[ratingScale] : undefined;
    const backgrounds = ratingScale ? RATING_BACKGROUNDS[ratingScale] : undefined;

    const evaluationLogEntries = buildEvaluationLogEntries(taskData?.evaluationLog ?? []);

    const handleEvidencePreview = (evidenceFileId: string | null) => {
        if (!evidenceFileId) {
            return;
        }

        const contentEntry = contentByIdUrl[evidenceFileId];

        if (contentEntry && isContentEntryValid(contentEntry)) {
            openInNewTab(contentEntry.tempUrl);

            return;
        }

        dispatch(resolveContentUri(evidenceFileId))
            .then((res) => {
                openInNewTab(res.url);
            })
            .catch(() => {});
    };

    const handlePrint = () => {
        if (!pathwayId || !taskId) {
            return;
        }

        openInNewTab(
            getTaskRoute(pathwayId, taskId, ROUTES.SIDEBAR.LEARNING_TASK.PRINT_EVALUATION_LOG, isAssessorView),
        );
    };

    useEffect(() => {
        if (!pathwayId || !taskId) {
            return;
        }

        if (isAssessorView) {
            void dispatch(
                getAssessorTask({
                    userPathwayId: Number(pathwayId),
                    pathwayItemId: Number(taskId),
                }),
            );

            return;
        }

        void dispatch(
            getLearnerTask({
                pathwayId,
                pathwayItemId: Number(taskId),
            }),
        );
        void dispatch(getTaskAssessors());
    }, [dispatch, pathwayId, taskId, isAssessorView]);

    return (
        <TaskEvaluationLogWrapper>
            {!!taskData && (
                <>
                    <TaskTitle>{taskData.details.taskName}</TaskTitle>

                    <EvaluationLogSection>
                        <PageTitle>{translateItem(SHARED_EVALUATION_LOG_PAGE_TITLE)}</PageTitle>

                        {evaluationLogEntries.map((entry) => {
                            const { attempt, status, isAssessorEvaluation, displayDateUTC, key } = entry;
                            const {
                                showStatusPill,
                                showAssessorComments,
                                statusLabel,
                                StatusIcon,
                                isInactiveRatings,
                                evidenceFileId,
                            } = getEvaluationLogEntryViewData(entry, STATUS_TEXT_LABELS);
                            const {
                                referenceText,
                                notProvidedText,
                                evidenceText,
                                assesseeCommentsLabel,
                                assesseeCommentsText,
                                assessorCommentsText,
                            } = getEvaluationLogFieldTexts(attempt, taskData.taskConfiguration.referenceText);

                            return (
                                <Log key={key} data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_ENTRY}>
                                    <LogHeader $status={status}>
                                        <LogInfoWrapper>
                                            <LogInfo>
                                                <LogInfoTextBold
                                                    $color={LOG_TITLE_COLOR_MAPPER[status]}
                                                    data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_ENTRY_TITLE}
                                                >
                                                    {getLogStatusText(status, attempt.number)}
                                                </LogInfoTextBold>

                                                <LogInfoText data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_ENTRY_PERSON}>
                                                    {getLogPersonText(
                                                        taskData.details.name,
                                                        taskData.details.rankName,
                                                        attempt.assessorId,
                                                        assessors,
                                                        attempt.assessorName,
                                                        attempt.assessorRank,
                                                        getPreviousAssessorText(
                                                            attempt.assessorId,
                                                            assessors,
                                                            taskData.assessorDelegations ?? [],
                                                        ),
                                                    )}
                                                </LogInfoText>
                                            </LogInfo>

                                            <LogInfo>
                                                <LogInfoText>
                                                    <DateWrapper data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_ENTRY_DATE}>
                                                        {formatDateBasedOnPreferences(displayDateUTC, dateFormat)}
                                                    </DateWrapper>
                                                </LogInfoText>

                                                {!!showStatusPill && (
                                                    <StatusInfoWrapper
                                                        data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_ENTRY_STATUS}
                                                    >
                                                        {!!StatusIcon && <StatusIcon />}

                                                        <StatusTextWrapper>
                                                            <LogInfoTextBold $color={LOG_TITLE_COLOR_MAPPER.TEXT_BODY}>
                                                                {t`Status: `}
                                                            </LogInfoTextBold>

                                                            <LogInfoText>{statusLabel}</LogInfoText>
                                                        </StatusTextWrapper>
                                                    </StatusInfoWrapper>
                                                )}
                                            </LogInfo>
                                        </LogInfoWrapper>
                                    </LogHeader>

                                    <LogContent>
                                        {!!ratings &&
                                            !!backgrounds &&
                                            generateUniqueKeys(taskData.taskConfiguration.subTasks).map(
                                                ({ item: task, key: taskKey }, taskIndex) => {
                                                    const learnerRating = attempt.learnerAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const assessorRating = attempt.assessorAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const selectedRating = isAssessorEvaluation
                                                        ? assessorRating
                                                        : learnerRating;
                                                    const compareRating = isAssessorEvaluation ? learnerRating : null;
                                                    const showCompareRating =
                                                        !isNil(compareRating) && compareRating !== selectedRating;

                                                    return (
                                                        <TaskWrapper
                                                            key={taskKey}
                                                            data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_TASK}
                                                        >
                                                            <Description>
                                                                {`${taskIndex + 1}.`} {task.description}
                                                            </Description>

                                                            <Guideline>{task.guideline}</Guideline>

                                                            <RatingsWrapper>
                                                                {ratings.map((rating, ratingIndex) => (
                                                                    <Rating
                                                                        key={rating}
                                                                        data-cy={
                                                                            TEST_IDS.LEARNING.EVALUATION_LOG_RATING
                                                                        }
                                                                        $isInactive={isInactiveRatings}
                                                                        $isSelected={
                                                                            !isInactiveRatings &&
                                                                            selectedRating === ratingIndex + 1
                                                                        }
                                                                        $hasAssessorOrAssesseeSelection={
                                                                            !isInactiveRatings &&
                                                                            !!showCompareRating &&
                                                                            compareRating === ratingIndex + 1
                                                                        }
                                                                        $selectedBackground={backgrounds[ratingIndex]}
                                                                    >
                                                                        {rating}
                                                                    </Rating>
                                                                ))}
                                                            </RatingsWrapper>
                                                        </TaskWrapper>
                                                    );
                                                },
                                            )}

                                        <SectionWrapper>
                                            <Label>{translateItem(SHARED_EVALUATION_LOG_REFERENCE)}</Label>

                                            <ReferenceContentWrapper>
                                                {referenceText ? (
                                                    <HtmlInjection text={referenceText} />
                                                ) : (
                                                    notProvidedText
                                                )}
                                            </ReferenceContentWrapper>
                                        </SectionWrapper>

                                        <SectionWrapper>
                                            <Label>{translateItem(SHARED_EVALUATION_LOG_EVIDENCE_LABEL)}</Label>

                                            <EvidenceWrapper>
                                                <BaseInput
                                                    value={evidenceText}
                                                    readOnly
                                                    disabled={evidenceFileId === null}
                                                    data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_EVIDENCE_NAME}
                                                />

                                                <EvidenceButtonsWrapper>
                                                    <BaseButton disabled>
                                                        {translateItem(SHARED_EVALUATION_LOG_REMOVE)}
                                                    </BaseButton>

                                                    <BaseButton
                                                        $customType="outline"
                                                        onClick={() => handleEvidencePreview(evidenceFileId)}
                                                        disabled={evidenceFileId === null}
                                                        iconPlacement="start"
                                                        icon={<EyeIcon />}
                                                        data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_EVIDENCE_PREVIEW}
                                                    />
                                                </EvidenceButtonsWrapper>
                                            </EvidenceWrapper>
                                        </SectionWrapper>

                                        <SectionWrapper>
                                            <Label>{assesseeCommentsLabel}</Label>

                                            <CommentsContent>{assesseeCommentsText}</CommentsContent>
                                        </SectionWrapper>

                                        {!!showAssessorComments && (
                                            <SectionWrapper>
                                                <Label>{translateItem(SHARED_EVALUATION_LOG_ASSESSOR_COMMENTS)}</Label>

                                                <CommentsContent>{assessorCommentsText}</CommentsContent>
                                            </SectionWrapper>
                                        )}
                                    </LogContent>
                                </Log>
                            );
                        })}

                        <PrintWrapper>
                            <BaseButton
                                onClick={handlePrint}
                                iconPlacement="end"
                                icon={<PrinterIcon />}
                                $customType="outline"
                                data-cy={TEST_IDS.LEARNING.EVALUATION_LOG_PRINT_BUTTON}
                            >
                                {translateItem(SHARED_EVALUATION_LOG_PRINT)}
                            </BaseButton>
                        </PrintWrapper>
                    </EvaluationLogSection>
                </>
            )}
        </TaskEvaluationLogWrapper>
    );
};
